import { VStack } from '@genial/components';
import { Box } from 'native-base';
import React, { useState, useEffect } from 'react';

import { PublicOfferApplicationProps, PublicOfferUserSelectionValues } from '../..';
import { Timeline } from '../../../../../../../../../../components/Timeline';
import {
  PublicOfferAssetToInvestNormalized,
  PublicOfferDetailsModalityWithTimelineNormalized,
} from '../../../../../../../../../../store/product/types';
import { ProductCard } from '../../../../components/ProductCard';
import { ExtraInfos } from '../ExtraInfos/ExtraInfos';
import { ModalitySelection } from '../ModalitySelection';
import { getInvestorTypeOptions, InvestorType } from '../ModalitySelection/constants';
import { UserSelections } from '../UserSelections';

import { getExtraInfos, getProductInfos } from './constants';

type Modality = PublicOfferDetailsModalityWithTimelineNormalized;

export interface PublicOfferInvestCardProps {
  offer: PublicOfferApplicationProps['offer'];
  setUserSelectionValues: React.Dispatch<React.SetStateAction<PublicOfferUserSelectionValues>>;
  selectedModality: Modality | undefined;
  setSelectedModality: React.Dispatch<React.SetStateAction<Modality | undefined>>;
  publicOfferAssetDetails?: PublicOfferAssetToInvestNormalized;
  publicOfferAssetDetailsLoading: boolean;
}

export const PublicOfferInvestCard: React.FC<PublicOfferInvestCardProps> = ({
  offer,
  selectedModality,
  setSelectedModality,
  setUserSelectionValues,
  publicOfferAssetDetails,
  publicOfferAssetDetailsLoading,
}) => {
  const [investorType, setInvestorType] = useState<InvestorType>();
  const [modalities, setModalities] = useState<Modality[]>([]);

  const handleSelectModality = (value: string) => {
    const selected = modalities.find((modality) => modality.codigoModalidade.toString() === value);
    setSelectedModality(selected);
  };

  useEffect(() => {
    if (publicOfferAssetDetails) {
      setSelectedModality(undefined);
      const { linkedModalities, unlinkedModalities } = publicOfferAssetDetails;
      if (unlinkedModalities.length > 0 && investorType === 'unlinked') {
        setModalities(unlinkedModalities);
      } else if (linkedModalities.length > 0 && investorType === 'linked') {
        setModalities(linkedModalities);
      } else {
        setModalities([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicOfferAssetDetails, investorType]);

  useEffect(() => {
    if (publicOfferAssetDetails) {
      const { linkedModalities, unlinkedModalities } = publicOfferAssetDetails;
      if (unlinkedModalities.length > 0) {
        return setInvestorType('unlinked');
      } else if (linkedModalities.length > 0) {
        return setInvestorType('linked');
      } else {
        return setInvestorType(undefined);
      }
    }
  }, [publicOfferAssetDetails]);

  return (
    <ProductCard
      infos={getProductInfos(offer.publicOfferType, publicOfferAssetDetails, selectedModality)}
      productColor="product.ofertapublica"
      title={offer.offerName}
      TopComponent={
        <ModalitySelection
          radioOptions={getInvestorTypeOptions(
            publicOfferAssetDetailsLoading,
            !!publicOfferAssetDetails?.linkedModalities?.length,
            !!publicOfferAssetDetails?.unlinkedModalities?.length
          )}
          investorType={investorType}
          setInvestorType={setInvestorType}
          modalities={modalities}
          isLoading={publicOfferAssetDetailsLoading}
          modalitySelected={selectedModality?.codigoModalidade.toString()}
          modalityDescription={selectedModality?.descricaoModalidade}
          setModalitySelected={handleSelectModality}
        />
      }
      BottomComponent={
        <VStack space="stack.xxxs">
          {!!selectedModality && (
            <Timeline
              title="Cronograma Indicativo da Oferta"
              timeline={selectedModality.timeline}
            />
          )}
          <Box w="full" h="1px" bgColor="neutral.light" />
          <ExtraInfos infos={getExtraInfos(offer.publicOfferType, publicOfferAssetDetails)} />
          <UserSelections
            offer={offer}
            offerDetails={publicOfferAssetDetails}
            selectedModality={selectedModality}
            setUserSelectionValues={setUserSelectionValues}
          />
        </VStack>
      }
    />
  );
};
